import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { saveStoreData } from "../../features/login/LoginSlice";

const PAGE_SIZE = 10;
const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 3;

export const useSelectStore = () => {
  const { authData } = useSelector((state) => state.loginSlice);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [initiallyHadData, setInitiallyHadData] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const debounceTimer = useRef(null);

  const fetchStores = async (searchQuery = "") => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiHandler({
        url: `${endpoint.API_BASE_URL}${endpoint.GET_STORES}?iPageSize=${PAGE_SIZE}&sSearchByName=${searchQuery}`,
        method: "GET",
        authToken: authData,
        dispatch,
      });

      if (response?.data?.status === "success") {
        const storeList = response?.data?.data?.storeData;
        const totalRecords = response?.data?.data?.totalRecords;
        setStores(storeList);
        setError(null);
        if (totalRecords > 0 && !initiallyHadData) {
          setInitiallyHadData(true);
        }
        setTotalCount(totalRecords);
      } else {
        setError(response?.data?.message);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch stores");
    } finally {
      setLoading(false);
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback((searchQuery) => {
    // Clear any existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Only make API call if search is empty (show all) or has minimum required characters
    if (searchQuery === "" || searchQuery.length >= MIN_SEARCH_LENGTH) {
      // Set new timer
      debounceTimer.current = setTimeout(() => {
        fetchStores(searchQuery);
      }, DEBOUNCE_DELAY);
    }
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchStores();
  }, []);

  const handleChangeStore = (store) => {
    dispatch(
      saveStoreData({
        storeId: store.iStoreId,
        storeName: store.sName,
      })
    );
    navigate("/productsandbox");
  };

  const handleStoreSearch = (e) => {
    const searchQuery = e.target.value;
    setSearch(searchQuery);
    debouncedSearch(searchQuery);
  };

  return {
    stores,
    loading,
    error,
    totalCount,
    handleChangeStore,
    handleStoreSearch,
    initiallyHadData,
    search,
    authData,
  };
};
